import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useTranslation("auth");
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error || t("login.errorGeneric"));
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Left visual panel */}
      <div className="hidden lg:flex flex-1 bg-zinc-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #2a2a2e 0%, transparent 40%), radial-gradient(circle at 80% 60%, #18181b 0%, transparent 50%)",
        }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3" data-testid="login-platform-brand">
            <img src="/brand/dih-icon-cor.png" alt="Do IT Happen" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-display font-bold text-2xl tracking-tight">PromoCrew</div>
              <div className="text-xs text-zinc-400 uppercase tracking-widest mt-1">by Do IT Happen</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight">
              {t("login.heroTitle")}
            </h1>
            <p className="text-zinc-400 mt-4 leading-relaxed text-sm">
              {t("login.heroSubtitle")}
            </p>
          </div>
          <div className="text-xs text-zinc-500">{t("login.copyright")}</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
        <form onSubmit={handleSubmit} className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 space-y-5" data-testid="login-form">
          <div className="space-y-1">
            <h2 className="font-display text-2xl font-bold text-zinc-950">{t("login.title")}</h2>
            <p className="text-sm text-zinc-500">{t("login.subtitle")}</p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("login.email")}</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
              placeholder={t("login.emailPlaceholder")}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("login.password")}</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950"
              placeholder={t("login.passwordPlaceholder")}
            />
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-zinc-950 hover:bg-zinc-800 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("login.submit")}
          </button>

          <div className="text-center pt-2">
            <a href="/forgot-password" data-testid="forgot-password-link" className="text-xs text-zinc-500 hover:text-zinc-900 underline">
              {t("login.forgotLink")}
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
